import { JSONSchema7 } from 'json-schema'

import {
    convertDecimalToFullNumberFraction,
    convertFullNumberFractionToDecimal,
    decimalNumberRegExp,
} from './numberConversion'
import { otherValue } from '../../shared/constants/general-enums/value-other'

interface OtherDimensionKeys {
    dimensionsKey: string
    thicknessOtherKey: string
    widthOtherKey: string
    /** convert the typed value (e.g. "1-1/4") to a decimal; an empty other value is then ignored */
    shouldConvertFractions?: boolean
}

/**
 * Value that replaces the 'other' placeholder of a dimension: the typed value as is,
 * or converted from a fraction to a decimal. An empty typed value is ignored when converting.
 */
const resolveOtherDimensionValue = (otherInput: string, shouldConvertFractions: boolean) => {
    if (!shouldConvertFractions) {
        return otherInput
    }

    return otherInput ? convertFullNumberFractionToDecimal(otherInput) : otherValue
}

/**
 * Replaces the 'other' placeholder in a dimensions object (thickness / width) with the value typed
 * into the matching `*_other` field, then clears those fields. Returns a new settings object.
 * Does nothing when the dimensions object is missing, so several dimension sets that share the same
 * `*_other` fields can be processed one after another as long as only one of them is filled.
 */
export const applyOtherDimensionValues = <T extends object>(
    settings: T,
    { dimensionsKey, thicknessOtherKey, widthOtherKey, shouldConvertFractions = false }: OtherDimensionKeys
): T => {
    const source = settings as Record<string, any>
    const dimensions = source[dimensionsKey]

    if (!dimensions) {
        return settings
    }

    const updatedDimensions = { ...dimensions }

    if (dimensions.thickness === otherValue) {
        updatedDimensions.thickness = resolveOtherDimensionValue(source[thicknessOtherKey], shouldConvertFractions)
    }
    if (dimensions.width === otherValue) {
        updatedDimensions.width = resolveOtherDimensionValue(source[widthOtherKey], shouldConvertFractions)
    }

    return {
        ...settings,
        [dimensionsKey]: updatedDimensions,
        [thicknessOtherKey]: null,
        [widthOtherKey]: null,
    }
}

/**
 * Sets the given `*_other` fields to null. Run it after all dimension sets that share these fields
 * were processed, so the fields are cleared even when no dimensions object was present.
 */
export const clearOtherDimensionFields = <T extends object>(settings: T, otherKeys: string[]): T => {
    return { ...settings, ...Object.fromEntries(otherKeys.map((key) => [key, null])) }
}

interface MoveToOtherDimensionKeys extends OtherDimensionKeys {
    /** JSON schema definition of the dimensions object (its `thickness` and `width` options) */
    definition: JSONSchema7 | null | undefined
}

type DimensionOptions = { enum?: unknown[]; enumNames?: string[] }

/**
 * Returns the value to store in the `*_other` field when a saved dimension is not one of the schema options,
 * or null when it should stay as it is.
 */
const getOtherInputForDimension = (
    value: unknown,
    options: DimensionOptions | undefined,
    shouldConvertFractions: boolean
): string | null => {
    if (!value || value === otherValue || !options) {
        return null
    }

    const rawValue = String(value)
    const otherInput =
        shouldConvertFractions && decimalNumberRegExp.test(rawValue)
            ? convertDecimalToFullNumberFraction(rawValue)
            : rawValue
    const isOption =
        options.enum?.includes(rawValue) || (shouldConvertFractions && options.enumNames?.includes(otherInput))

    return isOption ? null : otherInput
}

/**
 * Reverse of `applyOtherDimensionValues`: when a saved thickness / width is not one of the schema options,
 * moves it into the matching `*_other` field and sets the dimension to 'other'. Returns a new settings object.
 * With `shouldConvertFractions` the moved value is stored as a fraction (e.g. 1.75 -> 1-3/4)
 * and an option also matches by its fraction label (`enumNames`).
 */
export const moveToOtherDimensionFields = <T extends object>(
    settings: T,
    {
        dimensionsKey,
        thicknessOtherKey,
        widthOtherKey,
        definition,
        shouldConvertFractions = false,
    }: MoveToOtherDimensionKeys
): T => {
    const source = settings as Record<string, any>
    const dimensions = source[dimensionsKey]

    if (!dimensions) {
        return settings
    }

    const updatedSettings: Record<string, any> = { ...settings }
    const updatedDimensions = { ...dimensions }
    const properties = definition?.properties as Record<string, DimensionOptions> | undefined

    const thicknessOther = getOtherInputForDimension(
        dimensions.thickness,
        properties?.thickness,
        shouldConvertFractions
    )
    const widthOther = getOtherInputForDimension(dimensions.width, properties?.width, shouldConvertFractions)

    if (thicknessOther !== null) {
        updatedSettings[thicknessOtherKey] = thicknessOther
        updatedDimensions.thickness = otherValue
    }
    if (widthOther !== null) {
        updatedSettings[widthOtherKey] = widthOther
        updatedDimensions.width = otherValue
    }

    updatedSettings[dimensionsKey] = updatedDimensions

    return updatedSettings as T
}
